import { useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/hooks/useApi';
import { useModeAwareMutation, useModeAwareQuery } from '@/hooks/useModeAwareQuery';
import { getPromiseNotes } from '@/lib/demo-data/promise-notes';
import type { PromiseNote, PromiseNoteApiItem, PromiseNotesApiResponse } from '@/types/promise-note';
import { useBulkDeleteNotes } from './useBulkDeleteNotes';

function mapApiItemToPromiseNote(item: PromiseNoteApiItem): PromiseNote {
  return {
    id: String(item.id),
    promiseId: String(item.promiseToPayId),
    content: item.content,
    createdAt: item.createdAt,
    createdBy: {
      id: item.createdByUserId === null ? '' : String(item.createdByUserId),
      name: item.createdByUserName,
    },
  };
}

/**
 * Hook to fetch canonical promise notes (PromiseNotes table)
 */
export function usePromiseNotes(promiseId: string | undefined) {
  const { apiGet } = useApi();
  const mockNotes = promiseId ? getPromiseNotes(promiseId) : [];

  return useModeAwareQuery<PromiseNote[], Error>(
    ['promise-notes', promiseId],
    async ({ signal }) => {
      if (!promiseId) return [];
      const response = await apiGet<PromiseNotesApiResponse>(`/api/v1/promises/${promiseId}/notes`, undefined, signal);
      return (response.notes || []).map(mapApiItemToPromiseNote);
    },
    mockNotes,
    {
      enabled: !!promiseId,
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );
}

export interface UpdatePromiseNoteRequest {
  promiseId: string;
  noteId: string;
  content: string;
}

export interface DeletePromiseNoteRequest {
  promiseId: string;
  noteId: string;
}

function usePromiseNoteInvalidation() {
  const queryClient = useQueryClient();

  return (promiseId: string) => {
    queryClient.invalidateQueries({ queryKey: ['promise-notes', promiseId] });
    queryClient.invalidateQueries({ queryKey: ['promise-recent-activities'] });
    queryClient.invalidateQueries({ queryKey: ['promise-audit-logs'] });
  };
}

export function useUpdatePromiseNote() {
  const { apiPut } = useApi();
  const invalidate = usePromiseNoteInvalidation();

  return useModeAwareMutation<void, Error, UpdatePromiseNoteRequest>(
    async (request) => {
      await apiPut<unknown>(`/api/v1/promises/${request.promiseId}/notes/${request.noteId}`, {
        content: request.content,
      });
    },
    () => undefined,
    {
      onSuccess: (_, variables) => invalidate(variables.promiseId),
    },
  );
}

export function useDeletePromiseNote() {
  const { apiDelete } = useApi();
  const invalidate = usePromiseNoteInvalidation();

  return useModeAwareMutation<void, Error, DeletePromiseNoteRequest>(
    async (request) => {
      await apiDelete<void>(`/api/v1/promises/${request.promiseId}/notes/${request.noteId}`);
    },
    () => undefined,
    {
      onSuccess: (_, variables) => invalidate(variables.promiseId),
    },
  );
}

export function useDeleteBulkPromiseNotes() {
  return useBulkDeleteNotes('/api/v1/promises/notes/bulk-delete', [
    'promise-notes',
    'promise-recent-activities',
    'promise-audit-logs',
  ]);
}
